import { getUSANumberFormat, shouldShowDollarSymbol } from "app/utils/utils";
import { ProposalTypes } from "app/utils/types";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { getObjectFromLocalStore, setObjectInLocalStore } from "app/utils/localStore";
import { makeRequest } from "app/utils/makeRequest";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
}

export function isSplitDollar(proposalType: ProposalTypes) {
  return proposalType === ProposalTypes.SplitDollar;
}

export function isKeyPersonInsurance(proposalType: ProposalTypes) {
  return proposalType === ProposalTypes.KeyPersonInsurance;
}

export function parseDateString(dateString: string): Date {
  const datePart = dateString?.split("T")[0]; // strip time
  const [year, month, day] = datePart.split("-").map(Number);
  // Create date in local timezone to avoid timezone conversion issues
  // Use noon (12:00) to prevent any timezone edge cases that could shift the date
  return new Date(year, month - 1, day, 12, 0, 0, 0);
}
const safeBigIntToNumber = (value: number | bigint): number => {
  if (typeof value === "bigint") {
    return Number(value);
  }
  return value;
};
export const USFormatNumber = (value: number | bigint, category: string) => {
  const safeValue = safeBigIntToNumber(value);
  if (shouldShowDollarSymbol(category)) {
    return `$${getUSANumberFormat(safeValue, false)}`; // With $ symbol
  }
  return getUSANumberFormat(safeValue, false); // Without $ symbol, US format
};

export const checkChatConversationExists = async (
  firstUserID?: string,
  secondUserID?: string,
  neioAccessToken?: string,
) => {
  // Check if bot already has a conversation id or not
  const url = `https://dev.neio.core.iapp.cool/v1/workspace/conversation/check/${firstUserID}/${secondUserID}`;
  const getConversationResponse = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${neioAccessToken}`,
    },
  });
  const conversationData = await getConversationResponse.json();

  return conversationData;
};

export const setNeioAccessToken = async () => {
  // Use the normal baseURL with the neioAuthToken path
  const neioAuthQuery = `neio/auth-token`;
  const neioResponse = await makeRequest("virtualAdvisors").get(neioAuthQuery, true, false);
  const neioAccessTokenData = neioResponse?.data?.access_token || [];
  setObjectInLocalStore("neioAccessToken", neioAccessTokenData);
};

export const createChatConversation = async (
  firstUserID?: string,
  secondUserID?: string,
  neioAccessToken?: string,
  title?: string,
) => {
  const requestBody = {
    conversation: {
      created_by: firstUserID,
      title,
    },
    participants: [
      {
        participant_id: firstUserID,
      },
      {
        participant_id: secondUserID,
      },
    ],
  };

  const createConversationResponse = await fetch(
    "https://dev.neio.core.iapp.cool/v1/workspace/conversation",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${neioAccessToken}`,
      },
      body: JSON.stringify(requestBody),
    },
  ).then((res) => res.json());

  return createConversationResponse;
};

export const checkConversationIsBlocked = async (userId1: string, userId2: string) => {
  const url = `https://dev.neio.core.iapp.cool/v1/workspace/conversation/check-block/${userId1}/${userId2}`;
  const deleteConversationResponse = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getObjectFromLocalStore("neioAccessToken")}`,
    },
  });
  const conversationData = await deleteConversationResponse.json();

  return conversationData;
};

export const blockConversation = async (
  firstUserID: string,
  secondUserID: string,
  conversationId: string,
) => {
  const requestBody = {
    blocker: firstUserID,
    blocked: secondUserID,
    conversation_id: conversationId,
    block: true,
  };
  const url = `https://dev.neio.core.iapp.cool/v1/workspace/conversation/block`;
  const blockConversationResponse = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getObjectFromLocalStore("neioAccessToken")}`,
    },

    body: JSON.stringify(requestBody),
  });
  const conversationData = await blockConversationResponse.json();

  return conversationData;
};

export const unblockConversation = async (
  userId1: string,
  userId2: string,
  conversationId: string,
) => {
  const requestBody = {
    blocker: userId1,
    blocked: userId2,
    conversation_id: conversationId,
    block: false,
  };
  const url = `https://dev.neio.core.iapp.cool/v1/workspace/conversation/block`;
  const unblockConversationResponse = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getObjectFromLocalStore("neioAccessToken")}`,
    },
    body: JSON.stringify(requestBody),
  });
  const conversationData = await unblockConversationResponse.json();

  return conversationData;
};

export const getChartLabels = (data: any[], selectedChartFilter: string) => {
  if (selectedChartFilter === "insuredAge") {
    return data?.map((item: any) => `A${item?.insuredAge}`);
  }
  if (selectedChartFilter === "planYear") {
    return data?.map((item: any) => item?.planYear);
  }
  return data?.map((item: any) => {
    const curlyApostrophe = "\u2019"; // ’
    // If year is a number like 2025, return last two digits as string
    if (typeof item?.year === "number") {
      return curlyApostrophe + String(item.year).slice(-2);
    }
    // If year is a string, also slice last two digits
    if (typeof item?.year === "string") {
      return curlyApostrophe + item.year.slice(-2);
    }
    return item?.year;
  });
};
